import { useMutation, useQueryClient } from '@tanstack/react-query';
import { updateFolder } from '../api/folders.api';
import { useCurrentWorkspaceId } from '@/entities/workspace';
import { notificationService } from '@/lib/services/notification.service';
import type { FolderDto, FolderUpdateDto } from '@/lib/api/guidelines-client';
import { useTranslation } from 'react-i18next';

export interface UpdateFolderParams {
  folderId: string;
  title: string;
  description?: string;
}

/**
 * Hook to update an existing folder using TanStack Query mutation
 *
 * @returns TanStack Query mutation result with update folder functionality
 */
export const useUpdateFolder = () => {
  const { t } = useTranslation('knowledge');
  const queryClient = useQueryClient();
  const workspaceId = useCurrentWorkspaceId();

  return useMutation<FolderDto | null, Error, UpdateFolderParams>({
    mutationFn: async ({ folderId, title, description }: UpdateFolderParams) => {
      const updateDto: FolderUpdateDto = { title, description };
      return updateFolder(folderId, updateDto);
    },
    onSuccess: (data) => {
      // Invalidate folders query to refetch the list
      void queryClient.invalidateQueries({ queryKey: ['folders', workspaceId] });
      // Also invalidate the specific folder query
      if (data) {
        void queryClient.invalidateQueries({ queryKey: ['folder', data.id] });
        notificationService.showSuccess(
          t('guidelines.useUpdateFolder.folderUpdated', { title: data.title }),
          {
            anchorOrigin: { vertical: 'bottom', horizontal: 'center' },
          },
        );
      }
    },
    onError: (error) => {
      notificationService.showError(
        error.message || t('guidelines.useUpdateFolder.failedToUpdateFolder'),
      );
    },
  });
};
